<template>
    <div class="flex items-center gap-2">
        <button
            type="button"
            data-testid="feedback-thumb-up"
            :aria-pressed="value === 1"
            aria-label="Feedback thumbs up"
            :disabled="disabled"
            :class="buttonClass(value === 1, 'thumbs-up')"
            @click="() => handleThumbUp()">
            <i class="bi bi-hand-thumbs-up" aria-hidden="true"></i>
        </button>
        <BasePopover
            v-model:open="showFeedbackPopover"
            as-child
            side="top"
            :side-offset="8"
            align="end"
            content-class="z-50 max-w-md w-full bg-white rounded-xl border border-gray-200 shadow-lg p-0 overflow-hidden">
            <template #trigger>
                <button
                    type="button"
                    data-testid="feedback-thumb-down"
                    :aria-pressed="value === 0"
                    aria-label="Feedback thumbs down"
                    :disabled="disabled"
                    :class="buttonClass(value === 0, 'thumbs-down')"
                    @click="handleThumbDown">
                    <i class="bi bi-hand-thumbs-down" aria-hidden="true"></i>
                </button>
            </template>
            <template #content>
                <div class="p-4">
                    <FeedbackIssuesForm
                        target="insight"
                        :initial-comment="initialComment ?? undefined"
                        :initial-selected-issues="initialIssues ?? undefined"
                        @save="handleFeedbackFormSave"
                        @dismiss="closePopover" />
                </div>
            </template>
        </BasePopover>
    </div>
</template>

<script setup lang="ts">
import BasePopover from "~vue/components/BasePopover.vue";
import FeedbackIssuesForm from "~vue/Feedback/FeedbackIssuesForm.vue";
import { ref } from "vue";

interface Props {
    value: number | null;
    disabled?: boolean;
    initialComment?: string | null;
    initialIssues?: string[] | null;
}

const props = withDefaults(defineProps<Props>(), {
    value: null,
    disabled: false,
    initialComment: null,
    initialIssues: null,
});

const emit = defineEmits<{
    select: [value: 0 | 1];
    negativeFeedback: [payload: { comment?: string | null; issues?: string[] | null }];
}>();

const showFeedbackPopover = ref(false);

function closePopover() {
    showFeedbackPopover.value = false;
}

function handleThumbUp() {
    if (props.disabled) {
        return;
    }
    closePopover();
    if (props.value !== 1) {
        emit("select", 1);
    }
}

function handleThumbDown() {
    if (props.disabled) {
        return;
    }
    // Save thumbs down immediately with no comment/issues, then show popover for optional details
    if (props.value !== 0) {
        emit("select", 0);
    }
    showFeedbackPopover.value = true;
}

function handleFeedbackFormSave(payload: { issues: string[] | null; comments: string | null }) {
    emit("negativeFeedback", {
        comment: payload.comments ?? null,
        issues: payload.issues ?? null,
    });
    closePopover();
}

const buttonClass = (active: boolean, kind: "thumbs-up" | "thumbs-down") => {
    const base = "h-8 w-8 rounded-full border body-small flex items-center justify-center transition-colors";
    const styles = {
        inactive: "border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-300",
        "thumbs-up": "border-indigo-200 bg-indigo-50 text-indigo-600",
        "thumbs-down": "border-slate-300 bg-slate-100 text-slate-600",
    };
    return `${base} ${active ? styles[kind] : styles.inactive}`;
};
</script>
